import React, { useState, useEffect } from "react";
import {
  Box, Typography, Paper, CircularProgress, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Chip, Grid, TextField, InputAdornment, Divider,
  Button, Menu, MenuItem
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import { useParams, useNavigate } from "react-router-dom";
import { db } from "../firebase";
import { doc, getDoc, collection, query, where, onSnapshot } from "firebase/firestore";
import { verificaSessione, statisticheDem } from "../utils/otpClient";
import { minutiInStringa, secondiInHHMMSS, calcolaDurataEventoSecondi, nomeCognomePartecipante } from "../utils/parseReportPresenze";
import * as XLSX from "sheetjs-style";

export default function ReportCliente() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [errore, setErrore] = useState(null);
  const [evento, setEvento] = useState(null);
  const [partecipanti, setPartecipanti] = useState([]);
  const [stats, setStats] = useState(null);
  const [statsDEM, setStatsDEM] = useState(null);
  const [loadingDEM, setLoadingDEM] = useState(false);
  const [ricerca, setRicerca] = useState("");
  const [email, setEmail] = useState("");
  const [durataEventoSecondi, setDurataEventoSecondi] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);

  useEffect(() => {
    const token = sessionStorage.getItem(`clienteToken_${id}`);
    if (!token) { navigate(`/accesso/${id}`); return; }

    verificaSessione(token, id)
      .then((result) => {
        setEmail(result.email);
        return getDoc(doc(db, "eventi", id));
      })
      .then((snap) => {
        if (snap.exists()) {
          const eventoData = { id: snap.id, ...snap.data() };
          setEvento(eventoData);
          setDurataEventoSecondi(calcolaDurataEventoSecondi(eventoData));

          if (eventoData.mailupListaId) {
            setLoadingDEM(true);
            statisticheDem(token, id)
              .then(setStatsDEM)
              .catch((e) => console.error("Errore recupero statistiche DEM:", e))
              .finally(() => setLoadingDEM(false));
          }
        }
      })
      .catch(() => {
        sessionStorage.removeItem(`clienteToken_${id}`);
        navigate(`/accesso/${id}`);
      })
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (!id) return;
    const q = query(collection(db, "reportPresenze"), where("eventoId", "==", id));
    const unsub = onSnapshot(q, (snap) => {
      const p = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      p.sort((a, b) => (b.durataSecondi || b.durataMinuti * 60) - (a.durataSecondi || a.durataMinuti * 60));
      setPartecipanti(p);
      if (p.length > 0) {
        const durataMedia = Math.round(
          p.reduce((acc, x) => acc + x.durataMinuti, 0) / p.length
        );
        setStats({ totale: p.length, durataMedia });
      }
    });
    return unsub;
  }, [id]);

  function isRosso(p) {
    if (!durataEventoSecondi) return false;
    const soglia = durataEventoSecondi / 3;
    const durata = p.durataSecondi || p.durataMinuti * 60;
    return durata < soglia;
  }

  const partecipantiFiltrati = partecipanti.filter((p) => {
    const s = ricerca.toLowerCase();
    return p.nome?.toLowerCase().includes(s) || p.email?.toLowerCase().includes(s);
  });

function esportaPresenze() {
  const wb = XLSX.utils.book_new();

  // Data evento per nome foglio
  const dataEvento = evento?.dataInizio || evento?.data || "";
  const dataFormattata = dataEvento
    ? new Date(dataEvento).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit" }).replace(/\//g, "-")
    : "00-00-00";

  const soglia = durataEventoSecondi ? durataEventoSecondi / 3 : null;

  // Foglio 1: elenco partecipanti
  const foglio1Data = partecipanti.map((p) => {
    const { nome, cognome } = nomeCognomePartecipante(p);
    return {
      "Cognome": cognome,
      "Nome": nome,
      "Email": p.email || "",
      "Permanenza (hh:mm:ss)": secondiInHHMMSS(p.durataSecondi || p.durataMinuti * 60),
    };
  });

  const ws1 = XLSX.utils.json_to_sheet(foglio1Data);
  ws1["!cols"] = [{ wch: 20 }, { wch: 20 }, { wch: 40 }, { wch: 20 }];

  // Grassetto intestazioni foglio 1
  ["A1", "B1", "C1", "D1"].forEach((cell) => {
    if (ws1[cell]) ws1[cell].s = { font: { bold: true } };
  });

  // Evidenzia in giallo chi ha presenziato meno di 1/3
  if (soglia) {
    partecipanti.forEach((p, idx) => {
      const durata = p.durataSecondi || p.durataMinuti * 60;
      if (durata < soglia) {
        const row = idx + 2; // +2 perché riga 1 è intestazione
        ["A", "B", "C", "D"].forEach((col) => {
          const cell = `${col}${row}`;
          if (ws1[cell]) {
            ws1[cell].s = { fill: { fgColor: { rgb: "FFFF00" } } };
          }
        });
      }
    });
  }

  // Foglio 2: pivot per dominio
  const dominiCount = {};
  partecipanti.forEach((p) => {
    const dominio = p.email?.split("@")[1] || "sconosciuto";
    dominiCount[dominio] = (dominiCount[dominio] || 0) + 1;
  });
  const foglio2Data = Object.entries(dominiCount)
    .sort((a, b) => b[1] - a[1])
    .map(([dominio, count]) => ({ "Dominio": dominio, "Totale": count }));

  // Aggiungi riga totale complessivo
  const totaleComplessivo = Object.values(dominiCount).reduce((a, b) => a + b, 0);
  foglio2Data.push({ "Dominio": "Totale complessivo", "Totale": totaleComplessivo });

  const ws2 = XLSX.utils.json_to_sheet(foglio2Data);
  ws2["!cols"] = [{ wch: 35 }, { wch: 15 }];

  // Grassetto intestazioni e riga totale foglio 2
  ["A1", "B1"].forEach((cell) => {
    if (ws2[cell]) ws2[cell].s = { font: { bold: true } };
  });
  const rigaTotale = foglio2Data.length + 1;
  [`A${rigaTotale}`, `B${rigaTotale}`].forEach((cell) => {
    if (ws2[cell]) ws2[cell].s = { font: { bold: true } };
  });

  XLSX.utils.book_append_sheet(wb, ws1, `Report_Attendee_${dataFormattata}`);
  XLSX.utils.book_append_sheet(wb, ws2, "Partecipanti_per_Azienda");

  XLSX.writeFile(wb, `presenze_${evento?.titolo || id}.xlsx`);
}

function esportaStatistiche() {
  const wb = XLSX.utils.book_new();

  // Tabella 1: DEM invitati (righe 2-6)
  const tabella1 = [];
  if (statsDEM) {
    tabella1.push({ "Metrica": "Inviti spediti", "Valore": statsDEM.invito?.destinatari || 0 });
    tabella1.push({ "Metrica": "Inviti Consegnati", "Valore": statsDEM.totaleInvitati + statsDEM.confermati + statsDEM.nonConfermati });
    tabella1.push({ "Metrica": "Partecipanti confermati", "Valore": statsDEM.confermati });
    tabella1.push({ "Metrica": "Partecipanti non confermati", "Valore": statsDEM.nonConfermati });
    tabella1.push({ "Metrica": "Partecipanti da cui attendiamo risposta", "Valore": statsDEM.totaleInvitati });
  }

  // Tabella 2: Performance messaggi (righe 7-12)
  const tabella2 = [];
  if (statsDEM) {
    tabella2.push({ "Metrica": "INVITO - Aperture", "Valore": statsDEM.invito?.visualizzazioni || 0 });
    tabella2.push({ "Metrica": "INVITO - Click", "Valore": statsDEM.invito?.click || 0 });
    tabella2.push({ "Metrica": "REMINDER - Aperture", "Valore": statsDEM.reminder?.visualizzazioni || 0 });
    tabella2.push({ "Metrica": "REMINDER - Click", "Valore": statsDEM.reminder?.click || 0 });
    tabella2.push({ "Metrica": "PROMEMORIA - Aperture", "Valore": statsDEM.promemoria?.visualizzazioni || 0 });
    tabella2.push({ "Metrica": "PROMEMORIA - Click", "Valore": statsDEM.promemoria?.click || 0 });
  }

  // Tabella 3: Partecipazione (righe 13-15)
  const tabella3 = [];
  if (stats) {
    tabella3.push({ "Metrica": "Partecipanti unici", "Valore": stats.totale });
    tabella3.push({ "Metrica": "Durata media presenza (min)", "Valore": stats.durataMedia });
    if (statsDEM?.confermati > 0) {
      tabella3.push({ "Metrica": "Tasso partecipazione reale (%)", "Valore": Math.round(stats.totale / statsDEM.confermati * 100) });
    }
  }

  // Costruisci foglio unico con 3 tabelle separate da riga vuota
  const ws = XLSX.utils.aoa_to_sheet([]);
  let riga = 0;

  function aggiungiTabella(titolo, dati) {
    XLSX.utils.sheet_add_aoa(ws, [[titolo, ""]], { origin: { r: riga, c: 0 } });
    ws[XLSX.utils.encode_cell({ r: riga, c: 0 })].s = { font: { bold: true, sz: 12 } };
    riga++;

    XLSX.utils.sheet_add_aoa(ws, [["Metrica", "Valore"]], { origin: { r: riga, c: 0 } });
    ["A", "B"].forEach((col) => {
      const cell = `${col}${riga + 1}`;
      if (ws[cell]) ws[cell].s = { font: { bold: true } };
    });
    riga++;

    dati.forEach((d) => {
      XLSX.utils.sheet_add_aoa(ws, [[d["Metrica"], d["Valore"]]], { origin: { r: riga, c: 0 } });
      riga++;
    });

    riga++; // riga vuota tra tabelle
  }

  aggiungiTabella("DEM — Destinatari", tabella1);
  aggiungiTabella("DEM — Performance messaggi", tabella2);
  aggiungiTabella("Partecipazione", tabella3);

  ws["!cols"] = [{ wch: 35 }, { wch: 15 }];

  XLSX.utils.book_append_sheet(wb, ws, "Statistiche");
  XLSX.writeFile(wb, `statistiche_${evento?.titolo || id}.xlsx`);
}

  if (loading) return <Box display="flex" justifyContent="center" mt={8}><CircularProgress /></Box>;
  if (errore) return <Alert severity="error" sx={{ m: 4 }}>{errore}</Alert>;

  return (
    <Box sx={{ maxWidth: 960, mx: "auto", p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <svg width="120" viewBox="0 0 200 100" role="img">
          <defs>
            <linearGradient id="rcGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#E63329"/>
              <stop offset="50%" stopColor="#9B2DB5"/>
              <stop offset="100%" stopColor="#1A56A0"/>
            </linearGradient>
            <linearGradient id="rcBarGrad" x1="0%" y1="100%" x2="0%" y2="0%">
              <stop offset="0%" stopColor="#ffffff" stopOpacity="0.4"/>
              <stop offset="100%" stopColor="#ffffff" stopOpacity="0.95"/>
            </linearGradient>
          </defs>
          <rect x="4" y="4" width="60" height="60" rx="10" fill="url(#rcGrad)"/>
          <rect x="18" y="0" width="5" height="10" rx="2.5" fill="#ffffff" opacity="0.9"/>
          <rect x="44" y="0" width="5" height="10" rx="2.5" fill="#ffffff" opacity="0.9"/>
          <rect x="10" y="38" width="6" height="18" rx="2" fill="url(#rcBarGrad)"/>
          <rect x="20" y="30" width="6" height="26" rx="2" fill="url(#rcBarGrad)"/>
          <rect x="30" y="22" width="6" height="34" rx="2" fill="url(#rcBarGrad)"/>
          <rect x="40" y="28" width="6" height="28" rx="2" fill="url(#rcBarGrad)"/>
          <rect x="50" y="16" width="6" height="40" rx="2" fill="url(#rcBarGrad)"/>
          <polyline points="13,38 23,30 33,22 43,28 53,16"
            fill="none" stroke="#F59E0B" strokeWidth="1.5"
            strokeLinecap="round" strokeLinejoin="round"/>
          <circle cx="53" cy="16" r="2.5" fill="#F59E0B"/>
        </svg>
        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant="body2" color="text.secondary">
            Accesso come: <strong>{email}</strong>
          </Typography>
          <Button variant="outlined" size="small" startIcon={<DownloadIcon />}
            onClick={(e) => setMenuAnchor(e.currentTarget)}>
            Scarica report
          </Button>
          <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}>
            <MenuItem onClick={() => { esportaStatistiche(); setMenuAnchor(null); }}>
              Report statistiche
            </MenuItem>
            <MenuItem onClick={() => { esportaPresenze(); setMenuAnchor(null); }}
              disabled={partecipanti.length === 0}>
              Report presenze
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      {/* Titolo evento */}
      <Paper elevation={1} sx={{ p: 3, mb: 3, backgroundColor: "#f0f4ff" }}>
        <Typography variant="h5" fontWeight="bold" color="primary">
          {evento?.titolo || "—"}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {evento?.dataInizio || evento?.data || "—"}
        </Typography>
      </Paper>

      {/* STATISTICHE DEM */}
      {evento?.mailupListaId && (
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" fontWeight="bold" mb={2}>Statistiche DEM</Typography>
          {loadingDEM ? (
            <Box display="flex" justifyContent="center" py={3}><CircularProgress size={32} /></Box>
          ) : statsDEM ? (
            <>
              {/* KPI invitati */}
              <Grid container spacing={2} mb={3}>
                <Grid item xs={12} md={3}>
                  <Paper elevation={0} sx={{ p: 2, textAlign: "center", backgroundColor: "#E6F1FB", borderRadius: 2 }}>
                    <Typography variant="h4" fontWeight="bold" color="primary">
                      {statsDEM.totaleInvitati + statsDEM.confermati + statsDEM.nonConfermati}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">Totale destinatari</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Paper elevation={0} sx={{ p: 2, textAlign: "center", backgroundColor: "#f8f8f8", borderRadius: 2 }}>
                    <Typography variant="h4" fontWeight="bold" color="text.primary">
                      {statsDEM.totaleInvitati}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">In attesa di risposta</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Paper elevation={0} sx={{ p: 2, textAlign: "center", backgroundColor: "#EAF3DE", borderRadius: 2 }}>
                    <Typography variant="h4" fontWeight="bold" color="success.main">
                      {statsDEM.confermati}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Confermati ({statsDEM.totaleInvitati + statsDEM.confermati + statsDEM.nonConfermati > 0
                        ? Math.round(statsDEM.confermati / (statsDEM.totaleInvitati + statsDEM.confermati + statsDEM.nonConfermati) * 100)
                        : 0}%)
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Paper elevation={0} sx={{ p: 2, textAlign: "center", backgroundColor: "#FEF3E2", borderRadius: 2 }}>
                    <Typography variant="h4" fontWeight="bold" color="warning.main">
                      {statsDEM.nonConfermati}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">Non confermati</Typography>
                  </Paper>
                </Grid>
              </Grid>

              <Divider sx={{ mb: 3 }} />

              {/* Statistiche messaggi */}
              <Typography variant="subtitle2" fontWeight="bold" color="text.secondary"
                sx={{ mb: 2, textTransform: "uppercase", letterSpacing: 1, fontSize: 11 }}>
                Performance messaggi
              </Typography>
              <Grid container spacing={2} mb={2}>
                {statsDEM.invito && (
                  <>
                    <Grid item xs={12} md={4}>
                      <Paper elevation={0} sx={{ p: 2, backgroundColor: "#f8f8f8", borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary" display="block">INVITO — Destinatari</Typography>
                        <Typography variant="h5" fontWeight="bold">{statsDEM.invito.destinatari}</Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Paper elevation={0} sx={{ p: 2, backgroundColor: "#f8f8f8", borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary" display="block">INVITO — Aperture</Typography>
                        <Typography variant="h5" fontWeight="bold">
                          {statsDEM.invito.visualizzazioni}
                          <Typography component="span" variant="body2" color="text.secondary" ml={1}>
                            ({statsDEM.invito.destinatari > 0 ? Math.round(statsDEM.invito.visualizzazioni / statsDEM.invito.destinatari * 100) : 0}%)
                          </Typography>
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Paper elevation={0} sx={{ p: 2, backgroundColor: "#f8f8f8", borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary" display="block">INVITO — Click</Typography>
                        <Typography variant="h5" fontWeight="bold">
                          {statsDEM.invito.click}
                          <Typography component="span" variant="body2" color="text.secondary" ml={1}>
                            ({statsDEM.invito.destinatari > 0 ? Math.round(statsDEM.invito.click / statsDEM.invito.destinatari * 100) : 0}%)
                          </Typography>
                        </Typography>
                      </Paper>
                    </Grid>
                  </>
                )}
                {statsDEM.reminder && (
                  <>
                    <Grid item xs={12} md={6}>
                      <Paper elevation={0} sx={{ p: 2, backgroundColor: "#f8f8f8", borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary" display="block">REMINDER — Aperture</Typography>
                        <Typography variant="h5" fontWeight="bold">
                          {statsDEM.reminder.visualizzazioni}
                          <Typography component="span" variant="body2" color="text.secondary" ml={1}>
                            ({statsDEM.reminder.destinatari > 0 ? Math.round(statsDEM.reminder.visualizzazioni / statsDEM.reminder.destinatari * 100) : 0}%)
                          </Typography>
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Paper elevation={0} sx={{ p: 2, backgroundColor: "#f8f8f8", borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary" display="block">REMINDER — Click</Typography>
                        <Typography variant="h5" fontWeight="bold">
                          {statsDEM.reminder.click}
                          <Typography component="span" variant="body2" color="text.secondary" ml={1}>
                            ({statsDEM.reminder.destinatari > 0 ? Math.round(statsDEM.reminder.click / statsDEM.reminder.destinatari * 100) : 0}%)
                          </Typography>
                        </Typography>
                      </Paper>
                    </Grid>
                  </>
                )}
                {statsDEM.promemoria && (
                  <>
                    <Grid item xs={12} md={6}>
                      <Paper elevation={0} sx={{ p: 2, backgroundColor: "#f8f8f8", borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary" display="block">PROMEMORIA — Aperture</Typography>
                        <Typography variant="h5" fontWeight="bold">
                          {statsDEM.promemoria.visualizzazioni}
                          <Typography component="span" variant="body2" color="text.secondary" ml={1}>
                            ({statsDEM.promemoria.destinatari > 0 ? Math.round(statsDEM.promemoria.visualizzazioni / statsDEM.promemoria.destinatari * 100) : 0}%)
                          </Typography>
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Paper elevation={0} sx={{ p: 2, backgroundColor: "#f8f8f8", borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary" display="block">PROMEMORIA — Click</Typography>
                        <Typography variant="h5" fontWeight="bold">
                          {statsDEM.promemoria.click}
                          <Typography component="span" variant="body2" color="text.secondary" ml={1}>
                            ({statsDEM.promemoria.destinatari > 0 ? Math.round(statsDEM.promemoria.click / statsDEM.promemoria.destinatari * 100) : 0}%)
                          </Typography>
                        </Typography>
                      </Paper>
                    </Grid>
                  </>
                )}
                {statsDEM.incrementoConferme !== null && (
                  <Grid item xs={12}>
                    <Paper elevation={0} sx={{
                      p: 2,
                      backgroundColor: statsDEM.incrementoConferme >= 0 ? "#EAF3DE" : "#FDECEA",
                      borderRadius: 2
                    }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Incremento conferme dopo REMINDER
                      </Typography>
                      <Typography variant="h5" fontWeight="bold"
                        color={statsDEM.incrementoConferme >= 0 ? "success.main" : "error.main"}>
                        {statsDEM.incrementoConferme >= 0 ? "+" : ""}{statsDEM.incrementoConferme}%
                      </Typography>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">Statistiche DEM non disponibili.</Typography>
          )}
        </Paper>
      )}

      {/* STATISTICHE PARTECIPAZIONE */}
      {stats && (
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" fontWeight="bold" mb={2}>Statistiche partecipazione</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <Paper elevation={0} sx={{ p: 2, textAlign: "center", backgroundColor: "#E6F1FB", borderRadius: 2 }}>
                <Typography variant="h4" fontWeight="bold" color="primary">{stats.totale}</Typography>
                <Typography variant="body2" color="text.secondary">Partecipanti unici</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={3}>
              <Paper elevation={0} sx={{ p: 2, textAlign: "center", backgroundColor: "#EAF3DE", borderRadius: 2 }}>
                <Typography variant="h4" fontWeight="bold" color="success.main">
                  {minutiInStringa(stats.durataMedia)}
                </Typography>
                <Typography variant="body2" color="text.secondary">Durata media</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={3}>
              <Paper elevation={0} sx={{ p: 2, textAlign: "center", backgroundColor: "#FAEEDA", borderRadius: 2 }}>
                <Typography variant="h4" fontWeight="bold" color="warning.main">
                  {partecipanti.filter((p) => !isRosso(p)).length}
                </Typography>
                <Typography variant="body2" color="text.secondary">Presenti ≥ 1/3 durata</Typography>
              </Paper>
            </Grid>
            {statsDEM?.confermati > 0 && (
              <Grid item xs={12} md={3}>
                <Paper elevation={0} sx={{ p: 2, textAlign: "center", backgroundColor: "#F3E8FF", borderRadius: 2 }}>
                  <Typography variant="h4" fontWeight="bold" color="secondary.main">
                    {Math.round(stats.totale / statsDEM.confermati * 100)}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Tasso partecipazione reale</Typography>
                </Paper>
              </Grid>
            )}
          </Grid>
        </Paper>
      )}

      {/* LISTA PARTECIPANTI */}
      {partecipanti.length > 0 ? (
        <Paper elevation={1} sx={{ p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" fontWeight="bold">Elenco partecipanti</Typography>
            <Chip label={`${partecipantiFiltrati.length} risultati`} size="small" />
          </Box>
          <TextField fullWidth placeholder="Cerca per nome o email..."
            value={ricerca} onChange={(e) => setRicerca(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
            sx={{ mb: 2 }} size="small" />
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f0f4ff" }}>
                  <TableCell><strong>#</strong></TableCell>
                  <TableCell><strong>Cognome</strong></TableCell>
                  <TableCell><strong>Nome</strong></TableCell>
                  <TableCell><strong>Email</strong></TableCell>
                  <TableCell align="right"><strong>Permanenza</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {partecipantiFiltrati.map((p, idx) => {
                  const { nome, cognome } = nomeCognomePartecipante(p);
                  return (
                  <TableRow key={p.id} hover
                    sx={{ backgroundColor: isRosso(p) ? "#FDECEA" : "inherit" }}>
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell sx={{ color: isRosso(p) ? "error.main" : "inherit" }}>
                      {cognome || "—"}
                    </TableCell>
                    <TableCell sx={{ color: isRosso(p) ? "error.main" : "inherit" }}>
                      {nome || "—"}
                    </TableCell>
                    <TableCell sx={{ color: isRosso(p) ? "error.main" : "inherit" }}>
                      {p.email || "—"}
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        label={secondiInHHMMSS(p.durataSecondi || p.durataMinuti * 60)}
                        size="small"
                        color={isRosso(p) ? "error" : "success"}
                        variant="outlined"
                      />
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      ) : (
        <Paper elevation={1} sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary">
            Il report presenze non è ancora disponibile per questo evento.
          </Typography>
        </Paper>
      )}
    </Box>
  );
}